<template>
  <div class="dropdown w-full" :class="{ 'dropdown-open': isOpen }">
    <label class="input input-bordered flex w-full items-center gap-2">
      <input
        type="text"
        class="grow"
        :placeholder="placeholder"
        v-model="searchKeyword"
        @focus="isOpen = true"
        @input="onInput"
      />
      <span v-if="isLoading" class="loading loading-spinner loading-xs"></span>
      <i v-else class="fa-solid fa-search opacity-70"></i>
    </label>
    <ul
      v-if="isOpen && (versions.length > 0 || searchKeyword)"
      class="dropdown-content menu bg-base-100 rounded-box z-1 mt-1 max-h-60 w-full flex-nowrap overflow-y-auto p-2 shadow"
    >
      <li v-if="versions.length === 0 && !isLoading" class="disabled">
        <a>Không tìm thấy kết quả</a>
      </li>
      <li v-for="version in versions" :key="version.id">
        <a
          @click="selectVersion(version)"
          :class="{ active: modelValue === version.id }"
        >
          <div class="flex flex-col">
            <span class="line-clamp-1 font-bold">{{ version.version }}</span>
            <span class="line-clamp-2 text-xs opacity-70">{{
              version.description
            }}</span>
          </div>
        </a>
      </li>
      <li
        v-if="totalVersions > versions.length"
        class="disabled flex items-center justify-center py-2 text-xs opacity-50"
      >
        <span
          >và
          <strong class="text-sm">{{ totalVersions - versions.length }}</strong>
          phiên bản khác</span
        >
      </li>
    </ul>
  </div>
  <!-- Overlay to close dropdown when clicking outside -->
  <div v-if="isOpen" class="fixed inset-0 z-0" @click="isOpen = false"></div>
</template>

<script setup lang="ts">
import { axios_instance } from "@/api/api";
import type { version } from "@/components/VersionControl";
import { useDebounceFn } from "@vueuse/core";
import { onMounted, ref, watch } from "vue";

const props = defineProps<{
  modelValue: string;
  type: string;
  placeholder?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  select: [version: version];
}>();

const isOpen = ref(false);
const searchKeyword = ref("");
const versions = ref<version[]>([]);
const totalVersions = ref(0);
const isLoading = ref(false);

const fetchVersions = async (keyword: string) => {
  isLoading.value = true;
  try {
    const response = await axios_instance.post<{
      data: version[] | null;
      total_filter: number;
    }>("/api/v1/view/version/pagination", {
      keyword: keyword,
      page: 1,
      size: 10, // Limit to 10 as requested
      type: [props.type],
    });

    versions.value = response.data.data || [];
    totalVersions.value = response.data.total_filter || 0;
  } catch (error) {
    console.error("Error fetching versions:", error);
    versions.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Debounce the search to avoid too many API calls
const debouncedSearch = useDebounceFn((keyword: string) => {
  fetchVersions(keyword);
}, 300);

const onInput = () => {
  isOpen.value = true;
  debouncedSearch(searchKeyword.value);
};

const selectVersion = (v: version) => {
  emit("update:modelValue", v.id);
  emit("select", v);
  searchKeyword.value = v.version; // Display selected version name
  isOpen.value = false;
};

// Initial fetch or when type changes
watch(
  () => props.type,
  () => {
    searchKeyword.value = "";
    fetchVersions("");
  },
);

onMounted(() => {
  fetchVersions("");
});
</script>

<style scoped>
/* Ensure dropdown content is above the overlay */
.dropdown-content {
  z-index: 50; /* Higher than the overlay */
}
.fixed.inset-0.z-0 {
  z-index: 40; /* Lower than dropdown content but covers everything else */
  background: transparent; /* Invisible overlay */
}
</style>
